import React, { useEffect, useState } from "react";
import { Control, Controller, FieldValues, Path, PathValue, RegisterOptions, UseFormSetValue } from "react-hook-form";
import Autocomplete, { AutocompletePropsSizeOverrides } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import http from "../../commonFunctions/httpClient";
import { OverridableStringUnion } from "@mui/types";
import { SxProps } from "@mui/material";

export interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}


interface WSMultiAutoCompleteProps<
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues>
> {
  control?: Control<TFieldValues>;
  name: TFieldName;
  label: string;

  options?: Option[];
  defaultValue?: Option[];

  multiple?: boolean;
  disabled?: boolean;
  freeSolo?: boolean;

  setValue?: UseFormSetValue<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, TFieldName>;
  controllSize?: OverridableStringUnion<'small' | 'medium', {}>;

  source?: {
    url: string;
    pageSize: number;
    filter?: any[];
    sort?: string[];
  };

  transFormData?: (data: any) => Option[];
}


const WSMultiAutoComplete = <
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues>
>({
  control,
  name,
  label,
  options = [],
  defaultValue = [],
  multiple = true,
  disabled = false,
  freeSolo = false,
  rules,
  source,
  transFormData,
  controllSize = "small",
  setValue
}: WSMultiAutoCompleteProps<TFieldValues, TFieldName>) => {

  const [loading, setLoading] = useState<boolean>(!!source);
  const [newOptions, setNewOptions] = useState<Option[]>(options);

  // Set default value if provided
  useEffect(() => {
    if (setValue && defaultValue?.length && newOptions.length) {
      const mappedValues = defaultValue.map((id) =>
        newOptions.find((option) => option.value === id.value) || id
      );
      setValue?.(name, mappedValues as PathValue<TFieldValues, TFieldName>);
    }
  }, [setValue, name, defaultValue, newOptions]);

  // Fetch options from API if source provided
  useEffect(() => {
    if (source) {
      (async () => {
        try {
          const response: any = await http.post(source.url, {
            page: 1,
            pageSize: source.pageSize,
            filter: source.filter,
            sort: source.sort,
          });
          setNewOptions(transFormData ? transFormData(response.data) : response.data);
        } catch (error) {
          console.error("Error fetching options:", error);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  const commonFontSize: SxProps = {
    fontSize: '0.75rem',
    '& .MuiInputBase-input': { fontSize: '0.75rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
    '& .MuiAutocomplete-option': { fontSize: '0.75rem' },
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue as any} // TypeScript workaround for multi-select
      rules={rules}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...field}
          multiple={multiple}
          freeSolo={freeSolo}
          size={controllSize}
          options={newOptions}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.label
          }
          onChange={(_, value) => field.onChange(value)}
          disabled={disabled || loading}
          sx={commonFontSize}
          renderTags={(value: (Option | string)[], getTagProps) =>
            value.map((option, index) => (
              <Chip
                sx={{ fontSize: '0.75rem' }}
                label={typeof option === "string" ? option : option.label}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              sx={{ fontSize: '0.75rem' }}
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default WSMultiAutoComplete;
